import {
  Claim,
  EditClaimFormData,
  PatchClaimRequestBody,
  PostClaimApproveRequestBody,
  ClaimDetailInputField,
  ClaimNonOrderProductInputField
} from "src/types";
import { IdGenerator } from "src/utils";

export class EditClaimMapper {
  public static initClaimDetailInputField(issueType: "damage" | "discrepancy" = "damage"): ClaimDetailInputField {
    return {
      fieldId: IdGenerator.generateUniqueElementId(),
      claimType: issueType === "damage" ? "Physical Damage" : "Quantity Shortage",
      urgencyLevel: "Medium",
      detailedDescription: ""
    };
  }

  public static initEditClaimFormData(claim?: Claim): EditClaimFormData {
    const claimDetails: ClaimDetailInputField[] = claim?.claimDetails?.length
      ? claim.claimDetails.map((detail) => {
          const nonOrderProducts: ClaimNonOrderProductInputField[] | undefined = detail.nonOrderProducts?.map(
            (product) => ({
              fieldId: IdGenerator.generateUniqueElementId(),
              productId: product.productId,
              claimQuantity: product.claimQuantity.toString(),
              extraInfo: product.extraInfo || ""
            })
          );

          return {
            fieldId: IdGenerator.generateUniqueElementId(),
            claimType: detail.claimType,
            urgencyLevel: detail.urgencyLevel,
            detailedDescription: detail.detailedDescription,
            ...(nonOrderProducts && nonOrderProducts.length > 0 && { nonOrderProducts })
          };
        })
      : [
          {
            fieldId: IdGenerator.generateUniqueElementId(),
            claimType: claim?.claimDetails?.[0]?.claimType || "Physical Damage",
            urgencyLevel: claim?.claimDetails?.[0]?.urgencyLevel || "Medium",
            detailedDescription: claim?.claimDetails?.[0]?.detailedDescription || ""
          }
        ];

    return {
      issueType: claim?.issueType || "damage",
      products: claim?.products || [],
      claimDetails,
      warehouseLocation: claim?.warehouseLocation || "",
      claimImages: [],
      existingImageUrls: [],
      deletedImageUrls: [],
      rejectionReason: claim?.rejectionReason || ""
    };
  }

  public static patchClaimRequestBodyFromFormData(formData: EditClaimFormData): PatchClaimRequestBody {
    const products = formData.products
      ?.filter((product) => product.claimQuantity > 0)
      .map((product) => ({
        productId: product.productId,
        claimQuantity: product.claimQuantity
      }));

    const claimDetails = formData.claimDetails
      ?.filter((detail) => detail.detailedDescription.trim().length > 0)
      .map((detail) => {
        const mappedDetail: any = {
          claimType: detail.claimType,
          urgencyLevel: detail.urgencyLevel,
          detailedDescription: detail.detailedDescription.trim()
        };

        if (detail.nonOrderProducts && detail.nonOrderProducts.length > 0) {
          const validNonOrderProducts = detail.nonOrderProducts
            .filter((product) => {
              const productId = product.productId?.trim();
              const claimQty = product.claimQuantity?.trim();
              return productId && claimQty && !isNaN(parseInt(claimQty, 10)) && parseInt(claimQty, 10) > 0;
            })
            .map((product) => ({
              productId: product.productId.trim(),
              claimQuantity: parseInt(product.claimQuantity, 10),
              extraInfo: product.extraInfo?.trim() || ""
            }));

          if (validNonOrderProducts.length > 0) {
            mappedDetail.nonOrderProducts = validNonOrderProducts;
          }
        }

        return mappedDetail;
      });

    return {
      issueType: formData.issueType,
      products,
      claimDetails,
      warehouseLocation: formData.warehouseLocation.trim(),
      hasImages: formData.claimImages.length > 0 || formData.existingImageUrls.length > formData.deletedImageUrls.length
    };
  }

  public static approveClaimRequestBodyFromFormData(formData: EditClaimFormData): PostClaimApproveRequestBody {
    const products = formData.products
      ?.filter((product) => product.claimQuantity > 0)
      .map((product) => ({
        productId: product.productId,
        claimQuantity: product.claimQuantity
      }));

    const claimDetails = formData.claimDetails
      ?.filter((detail) => detail.detailedDescription.trim().length > 0)
      .map((detail) => {
        const mappedDetail: any = {
          claimType: detail.claimType,
          urgencyLevel: detail.urgencyLevel,
          detailedDescription: detail.detailedDescription.trim()
        };

        if (detail.nonOrderProducts && detail.nonOrderProducts.length > 0) {
          const validNonOrderProducts = detail.nonOrderProducts
            .filter((product) => {
              const productId = product.productId?.trim();
              const claimQty = product.claimQuantity?.trim();
              return productId && claimQty && !isNaN(parseInt(claimQty, 10)) && parseInt(claimQty, 10) > 0;
            })
            .map((product) => ({
              productId: product.productId.trim(),
              claimQuantity: parseInt(product.claimQuantity, 10),
              extraInfo: product.extraInfo?.trim() || ""
            }));

          if (validNonOrderProducts.length > 0) {
            mappedDetail.nonOrderProducts = validNonOrderProducts;
          }
        }

        return mappedDetail;
      });

    return {
      issueType: formData.issueType,
      products,
      claimDetails,
      warehouseLocation: formData.warehouseLocation.trim(),
      hasImages: formData.claimImages.length > 0 || formData.existingImageUrls.length > formData.deletedImageUrls.length
    };
  }
}
